import { useModal } from '@ebay/nice-modal-react';
import { BznavAppIds, getCookieData, requestGetFetch, setCookieData } from '@repo/common-utils';
import { useCommonRouter, useUTM } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { BoxButton, Checkbox, Divider, Icon, List, MainTitle, semanticColor, StickyBottomWrapper, SubTitle, useDialog, useToast } from '@repo/ui';
import { TermsAgreementPayload, useAuthContext } from '@repo/user-session';
import CryptoJS from 'crypto-js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import { APP_NAME, BZNAV_EMAIL_SIGN_UP_AES_KEY, BZNAV_LAST_SIGN_IN_COOKIE_KEY } from '../constants';
import { TermApprovalList, TermTypeList } from '../types';

import { TermDrawer } from './TermDrawer';

export const SignTerms = () => {
  const router = useCommonRouter();
  const { auth, authService, setStatusSignChecking } = useAuthContext();
  const { showDialog } = useDialog();
  const { toast } = useToast();
  const modal = useModal(TermDrawer, { link: '', keepMounted: true });
  const { utmData } = useUTM();
  const { sendClickEvent, sendMetaEvent, sendKakaoEvent } = useUserEventLogger();
  const [allTermList, setAllTermList] = useState<TermTypeList>({});
  const [termsApprovalState, setTermsApprovalState] = useState<TermApprovalList>({ required: {}, optional: {} });
  const requiredTermsValues = Object.values(termsApprovalState.required);
  const footerElement = document.getElementById('bznav-footer');
  const isAllRequiredTermsApproved = useMemo(
    () => requiredTermsValues.length > 0 && requiredTermsValues.every((term) => term.isApproved),
    [requiredTermsValues]
  );
  const isAllTermsApproved = useMemo(
    () => isAllRequiredTermsApproved && Object.values(termsApprovalState.optional).every((term) => term.isApproved),
    [isAllRequiredTermsApproved, termsApprovalState]
  );

  const sendSignUpEvent = () => {
    sendClickEvent('sign-up-terms_cta', { type: getCookieData(BZNAV_LAST_SIGN_IN_COOKIE_KEY) }, { isUseAirbridge: true });
    sendMetaEvent('SubmitApplication');
    sendKakaoEvent('completeRegistration');
  };

  const toggleTermsApprovalState = (termKey: string, isRequired: boolean = true) => {
    const newState = { ...termsApprovalState };
    if (termKey === 'all') {
      Object.keys(newState.required).forEach((key) => {
        const term = newState.required[key];
        if (term) term.isApproved = !isAllTermsApproved;
      });
      Object.keys(newState.optional).forEach((key) => {
        const term = newState.optional[key];
        if (term) term.isApproved = !isAllTermsApproved;
      });
    } else {
      const listType = isRequired ? 'required' : 'optional';
      const term = newState[listType][termKey];
      if (term) term.isApproved = !term.isApproved;
    }
    setTermsApprovalState(newState);
  };

  const getAllTermsState = () => {
    if (!isAllRequiredTermsApproved) {
      showDialog({ title: '모든 약관에 동의해주세요.' });
      return;
    }
    const requiredTerms: TermsAgreementPayload = Object.keys(termsApprovalState.required).map((key) => {
      return { key, ...termsApprovalState.required[key] };
    });
    const optionalTerms: TermsAgreementPayload = Object.keys(termsApprovalState.optional).map((key) => {
      return { key, ...termsApprovalState.optional[key] };
    });

    return requiredTerms.concat(optionalTerms);
  };

  const addSignedApp = async () => {
    const terms = getAllTermsState();
    if (!terms || !authService || !authService.userData) return;

    try {
      const returnUrl = await authService?.signService.addSignedApp(terms, utmData);
      sendSignUpEvent();
      router.replace(returnUrl ?? '/');
      setTimeout(() => {
        toast({
          variant: 'success',
          title: `${APP_NAME[authService.appId]} 서비스 이용 약관을 동의하였습니다.`
        });
      }, 1000);
    } catch (error: unknown) {
      console.error('addSignedApp Error:', error);
      showDialog({
        title: `${APP_NAME[authService.appId]} 이용 동의를 실패했어요`,
        description: '문제가 계속 되면 로그아웃 한 후 다시 로그인 해주세요.',
        buttons: {
          primary: {
            text: '확인',
            onClick: () => {
              router.back();
            }
          }
        }
      });
    }
  };

  const signUp = async () => {
    if (!router.query?.code && !router.query?.state) {
      showDialog({
        title: '회원가입에 필요한 필수 정보가 없습니다.\n다시 시도해주세요.',
        buttons: {
          primary: {
            onClick: () => router.back()
          }
        }
      });
      return;
    }
    try {
      const terms = getAllTermsState();
      if (!terms) return;

      const aesEncryptKey = localStorage.getItem(BZNAV_EMAIL_SIGN_UP_AES_KEY) ?? '';
      const state = router.query?.state
        ? JSON.parse(decodeURIComponent(CryptoJS.AES.decrypt((router.query.state as string).replace(/\s/g, '+'), aesEncryptKey).toString(CryptoJS.enc.Utf8)))
        : null;
      const returnUrl = await authService?.signService.startSignUp({
        code: router.query?.code as string,
        terms,
        ...state
      });

      if (returnUrl) {
        if (!state) {
          setCookieData(BZNAV_LAST_SIGN_IN_COOKIE_KEY, 'email', { maxAge: 60 * 60 * 24 * 30 });
        }
        sendSignUpEvent();
        setStatusSignChecking();
        router.replace(returnUrl);
        setTimeout(() => {
          toast({
            variant: 'success',
            title: '회원가입이 완료되었어요'
          });
        }, 1000);
        return;
      }
    } catch (error: unknown) {
      console.error('SignUp Error:', error);
      showDialog({
        title: '회원가입 요청을 실패했어요',
        description: '비정상적인 접근입니다.',
        buttons: {
          primary: {
            text: '확인',
            onClick: () => {
              router.back();
            }
          }
        }
      });
    } finally {
      localStorage.removeItem(BZNAV_EMAIL_SIGN_UP_AES_KEY);
    }
  };

  const getTermList = useCallback(async () => {
    const response = await requestGetFetch({ url: `${process.env.NEXT_PUBLIC_SSO_API_SERVER}/term/v2/app/${authService?.appId}` });
    if (response.ok) {
      const allTerms = await response.json();

      const typeList: TermTypeList = {};
      const approvalList: TermApprovalList = { required: {}, optional: {} };

      for (const term of allTerms.terms) {
        if (!term.type) continue;
        if (!typeList[term.type]) typeList[term.type] = [];
        typeList[term.type]?.push(term);

        const approvalType = term.isRequired ? 'required' : 'optional';
        approvalList[approvalType][term.key] = { isApproved: false, version: term.version };
      }

      setAllTermList(typeList);
      setTermsApprovalState(approvalList);
      return;
    }
    throw new Error('Failed to fetch terms');
  }, [authService?.appId]);

  useEffect(() => {
    getTermList();
  }, [getTermList]);

  return (
    <>
      <div className={'flex-1'}>
        <MainTitle size={'medium'} title={'서비스를 이용하기 전에\n약관에 먼저 동의해주세요'} className={'mb-4'} />
        <List
          title={'약관에 모두 동의'}
          leadingElement={<Checkbox checked={isAllTermsApproved} isBox />}
          primaryAction
          onClick={() => toggleTermsApprovalState('all')}
        />

        {Object.keys(allTermList).map((key) => {
          const sectionCategory = key === 'common' ? '비즈넵' : (APP_NAME[authService?.appId as BznavAppIds] ?? '');
          return (
            <div key={key}>
              <Divider />
              <div className="mb-normal-large">
                <SubTitle title={`${sectionCategory} 이용약관`} />
                {allTermList[key]?.map((termInfo) => (
                  <List
                    key={termInfo.key}
                    size={'xsmall'}
                    className={'w-full py-2'}
                    leadingElement={<Checkbox checked={termsApprovalState[termInfo.isRequired ? 'required' : 'optional'][termInfo.key]?.isApproved || false} />}
                    title={`${termInfo.isRequired ? '[필수]' : '[선택]'} ${termInfo.name}`}
                    secondaryAction={
                      <div
                        onClick={() => {
                          modal.show({ link: termInfo.popupUrl, keepMounted: true });
                        }}
                      >
                        <Icon size={16} height={16} type={'chevron-right'} color={semanticColor.neutral.foreground.lowest} />
                      </div>
                    }
                    primaryAction
                    onClick={() => {
                      toggleTermsApprovalState(termInfo.key, termInfo.isRequired);
                    }}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {footerElement &&
        createPortal(
          <StickyBottomWrapper className={'!sticky'}>
            <BoxButton size={'large'} className={'w-full'} disabled={!isAllRequiredTermsApproved} onClick={auth.status === 'login' ? addSignedApp : signUp}>
              {isAllRequiredTermsApproved ? '완료' : '약관에 동의해주세요'}
            </BoxButton>
          </StickyBottomWrapper>,
          footerElement
        )}
    </>
  );
};
